import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Alert,
  Box,
  Container,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BusinessIcon from "@mui/icons-material/Business";
import Navbar from "../../Navbar";
import { UrlApi } from "../../../services/apirest";
import { authHeader } from "../../../services/services";
import TabSeleccionEmpresa, {
  idDeEmpresa,
  nombreDeEmpresa,
} from "./TabSeleccionEmpresa";
import ImportarArcaDialog from "./ImportarArcaDialog";
import ListadoPendientesArca from "./ListadoPendientesArca";
import ArcaEncontradoDialog from "./ArcaEncontradoDialog";
import AddComprobantesArca from "../../compras/comprobantes/arca/AddComprobantesArca";

const PASOS = ["Empresa", "Comprobantes"];

const SECTOR_GRANEL = "12";
const SECTOR_COMPRAS = "4";

export default function FlujoImportacionArca() {
  const navigate = useNavigate();
  const sector = localStorage.getItem("sector");
  const esGranel = sector === SECTOR_GRANEL;
  const esCompras = sector === SECTOR_COMPRAS;
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [errorEmpresas, setErrorEmpresas] = useState("");
  const [empresaSel, setEmpresaSel] = useState(null);
  const [paso, setPaso] = useState(0);
  const [openImportar, setOpenImportar] = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [countPend, setCountPend] = useState(null);
  const [filaArca, setFilaArca] = useState(null);
  const [prefillManual, setPrefillManual] = useState(null);
  const [encontrado, setEncontrado] = useState(null);
  const listadoRef = useRef(null);

  useEffect(() => {
    let activo = true;
    setLoadingEmpresas(true);
    fetch(UrlApi + "/empresas")
      .then((r) => r.json())
      .then((d) => {
        if (!activo) return;
        setEmpresas(Array.isArray(d) ? d : []);
        setErrorEmpresas("");
      })
      .catch((err) => {
        console.error("Error al cargar empresas:", err);
        if (activo) setErrorEmpresas("No se pudieron cargar las empresas.");
      })
      .finally(() => activo && setLoadingEmpresas(false));
    return () => {
      activo = false;
    };
  }, []);

  const idEmpresaSel = empresaSel ? idDeEmpresa(empresaSel) : null;
  const nombreEmpresaSel = empresaSel ? nombreDeEmpresa(empresaSel) : "";

  const refrescarPendientes = () => {
    if (idEmpresaSel == null) return;
    fetch(UrlApi + `/comprobantes_arca/pendientes/count?id_empresa=${idEmpresaSel}`, {
      headers: authHeader(),
    })
      .then((r) => r.json())
      .then((d) => setCountPend(d ?? null))
      .catch(() => setCountPend(null));
    listadoRef.current?.refrescar();
  };

  useEffect(() => {
    if (paso === 1 && idEmpresaSel != null) {
      fetch(UrlApi + `/comprobantes_arca/pendientes/count?id_empresa=${idEmpresaSel}`, {
        headers: authHeader(),
      })
        .then((r) => r.json())
        .then((d) => setCountPend(d ?? null))
        .catch(() => setCountPend(null));
    }
  }, [paso, idEmpresaSel]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f0f4f8" }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ pt: 2.5, pb: mostrarForm ? 0 : 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: "#1f618d" }}
          >
            Volver
          </Button>
          <Typography variant="h6" fontWeight={700} color="#0f2240">
            Importar comprobantes desde ARCA
          </Typography>
        </Box>

        <Stepper activeStep={paso} sx={{ mb: mostrarForm ? 1 : 3 }}>
          {PASOS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {paso === 0 && (
          <TabSeleccionEmpresa
            empresas={empresas}
            loading={loadingEmpresas}
            error={errorEmpresas}
            idEmpresaSeleccionada={idEmpresaSel}
            onSelect={(emp) => setEmpresaSel(emp)}
            onContinuar={() => setPaso(1)}
          />
        )}

        {paso === 1 && !mostrarForm && (
          <Stack spacing={2}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #e0e0e0",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <BusinessIcon sx={{ color: "#1f618d" }} />
                <Typography variant="body2" color="text.secondary">
                  Empresa seleccionada:
                </Typography>
                <Chip label={nombreEmpresaSel} color="primary" />
              </Stack>
              <Button size="small" onClick={() => setPaso(0)}>
                Cambiar empresa
              </Button>
            </Paper>

            {esGranel && (
              <Paper elevation={1} sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Carga del archivo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Seleccioná el archivo CSV descargado de ARCA correspondiente a esta
                  empresa. Una vez procesado, el sector Compras podrá registrar cada
                  comprobante con los datos autocompletados desde el archivo.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setOpenImportar(true)}
                >
                  Importar CSV de ARCA
                </Button>
              </Paper>
            )}

            {!esGranel && !esCompras && (
              <Paper elevation={1} sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                <Alert severity="info">
                  No tenés permisos para operar en este módulo. La carga del CSV la
                  realiza el sector Granel y el registro de comprobantes, el sector Compras.
                </Alert>
              </Paper>
            )}

            <Paper elevation={1} sx={{ p: 3, border: "1px solid #e0e0e0" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  {esCompras ? "Buscar y cargar comprobante" : "Comprobantes sin procesar"}
                </Typography>
                {countPend != null && (
                  <Alert severity={countPend.count > 0 ? "info" : "success"} sx={{ py: 0 }}>
                    {countPend.count > 0
                      ? `Hay ${countPend.count} comprobantes sin procesar` +
                        (countPend.completos
                          ? ` (${countPend.completos} listos para cargar).`
                          : ".")
                      : "No hay comprobantes pendientes."}
                  </Alert>
                )}
              </Stack>
              <ListadoPendientesArca
                ref={listadoRef}
                idEmpresa={idEmpresaSel}
                puedeCargar={esCompras}
                onCargar={(row) => {
                  setFilaArca(row);
                  setPrefillManual(null);
                  setMostrarForm(true);
                }}
                onEncontrado={(row) => setEncontrado(row)}
                onCargarManual={(prefill) => {
                  setFilaArca(null);
                  setPrefillManual(prefill);
                  setMostrarForm(true);
                }}
              />
            </Paper>
          </Stack>
        )}

      </Container>

      {paso === 1 && mostrarForm && (
        <AddComprobantesArca
          empresaFija={{ id: idEmpresaSel, nombre: nombreEmpresaSel }}
          filaArca={filaArca}
          prefillManual={prefillManual}
          alta={setMostrarForm}
          tabla={() => {
            setMostrarForm(false);
            setFilaArca(null);
            setPrefillManual(null);
            refrescarPendientes();
          }}
        />
      )}

      <ImportarArcaDialog
        open={openImportar}
        setOpen={setOpenImportar}
        idEmpresa={idEmpresaSel}
        empresaNombre={nombreEmpresaSel}
        onImported={refrescarPendientes}
      />

      <ArcaEncontradoDialog
        open={!!encontrado}
        row={encontrado}
        onClose={() => setEncontrado(null)}
        onAceptar={() => {
          setFilaArca(encontrado);
          setPrefillManual(null);
          setEncontrado(null);
          setMostrarForm(true);
        }}
      />
    </Box>
  );
}
